// ═══════════════════════════════════════════════════════════════
// WEALTH IMPORT PANEL — Settings → Data
// Path: src/components/settings/WealthImportPanel.jsx
//
// Admin-only UI to import a DonorSearch CSV (or any vendor file
// with the mapped columns). Three-step workflow:
//   1. Upload → parse headers + rows
//   2. Preview → show match counts (email / name+city / unmatched)
//   3. Commit → bulk upsert to donor_wealth_profile
//
// Re-import semantics: matched members are OVERWRITTEN (latest
// wins). raw_import preserves the original row.
// Unmatched rows are DROPPED with a warning count. v1 does not
// create new donors from the CSV.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef } from "react";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Database,
  X,
  Info,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { InfoBubble } from "../ui/InfoBubble";
import { previewImport } from "../../utils/donorSearchMapping";
import { wealthProfileService } from "../../services/wealthProfileService";

const TIER_META = {
  email: { label: "Matched by email", color: "#10b981" },
  name_city: { label: "Matched by name + city", color: "#10b981" },
  name_only: { label: "Matched by name only", color: "#f59e0b" },
  ambiguous: { label: "Ambiguous name match (dropped)", color: "#ef4444" },
  none: { label: "No match (dropped)", color: "#94a3b8" },
};

export const WealthImportPanel = ({ palette }) => {
  const { members, currentUser, allWealthProfiles, refreshWealthProfiles, showMessage } = useApp();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = previewImport(text, members);
      setPreview(result);
    } catch (err) {
      setError(err.message || "Failed to parse CSV");
      setPreview(null);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const toSave = preview.rows
        .filter(
          (r) =>
            r.matchedMember &&
            (r.matchTier === "email" ||
              r.matchTier === "name_city" ||
              r.matchTier === "name_only")
        )
        .map((r) => ({
          memberId: r.matchedMember.id,
          ...r.wealth,
          rawImport: r.raw,
        }));
      const result = await wealthProfileService.bulkUpsert(
        toSave,
        currentUser?.id,
        "donorsearch"
      );
      await refreshWealthProfiles();
      showMessage(
        `Imported ${result.saved} wealth profile${result.saved === 1 ? "" : "s"}`
      );
      setPreview(null);
      setFileName("");
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
    setFileName("");
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const importableCount = preview
    ? preview.counts.matchedEmail +
      preview.counts.matchedNameCity +
      preview.counts.matchedNameOnly
    : 0;

  // Count matched rows that will overwrite a manual-research profile.
  // Import is still allowed — this is a transparent warning only, so
  // directors aren't surprised by losing hand-collected research.
  const manualOverwriteCount = preview
    ? preview.rows.filter((r) => {
        if (!r.matchedMember) return false;
        if (
          r.matchTier !== "email" &&
          r.matchTier !== "name_city" &&
          r.matchTier !== "name_only"
        )
          return false;
        const existing = allWealthProfiles?.[r.matchedMember.id];
        return existing && existing.source === "manual";
      }).length
    : 0;

  return (
    <Card style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <Database size={ICON_SIZES.md} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.base,
            fontWeight: 700,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
          }}
        >
          Wealth Screening Import
        </div>
        <InfoBubble
          size={ICON_SIZES.sm}
          text="Import a DonorSearch CSV (iWave and WealthEngine exports also work for common columns). Data is matched to existing donors by email first, then name + city. Re-importing a refreshed file overwrites the previous data — the raw row is preserved for audit."
        />
      </div>
      <p
        style={{
          color: palette.text2,
          fontSize: FONT_SIZES.sm,
          margin: "0 0 14px 0",
          lineHeight: 1.5,
        }}
      >
        Upload a DonorSearch export (or any compatible wealth-screen CSV). We'll
        match rows to your existing donors, show you the counts, and commit
        once you confirm. Unmatched rows are dropped with a warning — this
        tool does not create new donors.
      </p>

      {!preview && !error && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />
          <ActionButton
            label="Choose CSV file"
            icon={Upload}
            onClick={() => fileRef.current?.click()}
          />
        </div>
      )}

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            padding: 12,
            borderRadius: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontSize: FONT_SIZES.sm,
            marginBottom: 12,
          }}
        >
          <AlertCircle size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>{error}</div>
          <button
            onClick={handleReset}
            style={{
              background: "transparent",
              border: "none",
              color: "#991b1b",
              cursor: "pointer",
            }}
          >
            <X size={ICON_SIZES.sm} />
          </button>
        </div>
      )}

      {preview && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 10,
              borderRadius: 8,
              background: `${palette.accent1}08`,
              border: `1px solid ${palette.cardBorder}`,
              marginBottom: 14,
              fontSize: 12,
              color: palette.text2,
            }}
          >
            <FileText size={ICON_SIZES.sm} color={palette.accent1} />
            <strong style={{ color: palette.text1 }}>{fileName}</strong>
            <span>· {preview.counts.total} rows parsed</span>
            <span style={{ marginLeft: "auto" }}>
              {preview.headers.length} columns ·{" "}
              {preview.unknownHeaders.length} unmapped
            </span>
          </div>

          {/* Match breakdown */}
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: palette.text3,
                letterSpacing: 0.4,
                marginBottom: 6,
              }}
            >
              MATCH BREAKDOWN
            </div>
            {Object.keys(TIER_META).map((tier) => {
              const count =
                tier === "email"
                  ? preview.counts.matchedEmail
                  : tier === "name_city"
                  ? preview.counts.matchedNameCity
                  : tier === "name_only"
                  ? preview.counts.matchedNameOnly
                  : tier === "ambiguous"
                  ? preview.counts.ambiguous
                  : preview.counts.unmatched;
              if (count === 0) return null;
              const meta = TIER_META[tier];
              return (
                <div
                  key={tier}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 10px",
                    borderRadius: 6,
                    marginBottom: 4,
                    background: `${meta.color}12`,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: meta.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: FONT_SIZES.sm,
                      color: palette.text2,
                    }}
                  >
                    {meta.label}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontWeight: 800,
                      color: palette.text1,
                      fontSize: FONT_SIZES.sm,
                    }}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>

          {preview.unknownHeaders.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: 10,
                borderRadius: 8,
                background: "#fef3c7",
                border: "1px solid #fde68a",
                fontSize: 12,
                color: "#78350f",
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            >
              <Info size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong>{preview.unknownHeaders.length} unmapped columns</strong>{" "}
                will be preserved in the raw import log but won't contribute to
                structured fields:{" "}
                <em>{preview.unknownHeaders.slice(0, 6).join(", ")}</em>
                {preview.unknownHeaders.length > 6 &&
                  ` (+${preview.unknownHeaders.length - 6} more)`}
              </div>
            </div>
          )}

          {manualOverwriteCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: 10,
                borderRadius: 8,
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                fontSize: 12,
                color: "#78350f",
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            >
              <AlertCircle
                size={ICON_SIZES.sm}
                style={{ marginTop: 2, flexShrink: 0, color: "#f59e0b" }}
              />
              <div>
                <strong>
                  {manualOverwriteCount} donor{manualOverwriteCount === 1 ? "" : "s"} have manual research that will be overwritten
                </strong>{" "}
                by this import. The original row is preserved in raw_import
                for forensic recovery, but the structured fields
                you entered by hand will be replaced by vendor data on commit.
              </div>
            </div>
          )}

          {/* Sample preview — first 3 importable rows */}
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: palette.text3,
                letterSpacing: 0.4,
                marginBottom: 6,
              }}
            >
              SAMPLE PREVIEW (first 3 importable rows)
            </div>
            {preview.rows
              .filter(
                (r) =>
                  r.matchedMember &&
                  (r.matchTier === "email" ||
                    r.matchTier === "name_city" ||
                    r.matchTier === "name_only")
              )
              .slice(0, 3)
              .map((r, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    background: palette.cardBg,
                    border: `1px solid ${palette.cardBorder}`,
                    marginBottom: 6,
                    fontSize: 12,
                    color: palette.text2,
                    lineHeight: 1.5,
                  }}
                >
                  <div>
                    <strong style={{ color: palette.text1 }}>
                      {r.matchedMember.firstName} {r.matchedMember.lastName}
                    </strong>
                    <span style={{ marginLeft: 8, color: palette.text3, fontSize: 11 }}>
                      via {r.matchTier.replace("_", " + ")}
                    </span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    {r.wealth.capacityRating && (
                      <span>Rating: {r.wealth.capacityRating} · </span>
                    )}
                    {r.wealth.realEstateValue != null && (
                      <span>
                        RE:{" "}
                        {r.wealth.realEstateValue.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })}{" "}
                        ·{" "}
                      </span>
                    )}
                    {r.wealth.politicalGivingTotal != null && (
                      <span>
                        FEC:{" "}
                        {r.wealth.politicalGivingTotal.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              paddingTop: 12,
              borderTop: `1px solid ${palette.cardBorder}`,
            }}
          >
            <ActionButton
              label="Discard"
              icon={X}
              variant="secondary"
              onClick={handleReset}
              disabled={saving}
            />
            <ActionButton
              label={
                saving
                  ? "Importing..."
                  : `Import ${importableCount} profile${importableCount === 1 ? "" : "s"}`
              }
              icon={CheckCircle}
              onClick={handleCommit}
              disabled={saving || importableCount === 0}
            />
          </div>
        </div>
      )}
    </Card>
  );
};
